import { AddOutlined, DeleteOutlined } from '@mui/icons-material';
import { Box, Button, Chip, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { FieldArray, useFormikContext } from 'formik';

import { BillingScheduleBar } from './billingSchedule';
import InstallmentStatusSelect from './installmentStatusSelect';
import FormField from 'components/common/formikForm/formField';

import { BILLABLE_CASE_STATUSES } from 'data/constants/serviceProviderBilling';
import {
    getInstallmentAmount,
    getInstallmentsTotal,
    getRemainingPercentage,
} from 'helpers/serviceProviderBilling';
import useTranslate from 'hooks/useTranslate';
import type { BillingInstallmentFormValues, BillingRuleFormValues } from 'types';
import { formatCurrency } from 'utils/formatters';

interface InstallmentsEditorProps {
    disabled?: boolean;
}

const InstallmentsEditor = ({ disabled = false }: InstallmentsEditorProps) => {
    const { translate } = useTranslate();
    const { values, errors, submitCount } = useFormikContext<BillingRuleFormValues>();
    const price = Number(values.price) || 0;
    const total = getInstallmentsTotal(values.installments);
    const scheduleError =
        submitCount > 0 && typeof errors.installments === 'string' ? errors.installments : null;
    const preview = values.installments.map((installment) => ({
        statuses: installment.statuses,
        percentage: Number(installment.percentage) || 0,
    }));

    return (
        <Stack spacing={1.5}>
            <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {translate('serviceProviderBilling.schedule.title')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {translate('serviceProviderBilling.schedule.description')}
                </Typography>
            </Box>

            <FieldArray name="installments">
                {({ push, remove }) => (
                    <Stack spacing={1}>
                        {values.installments.map((installment, index) => (
                            <Stack
                                key={index}
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={{ xs: 0.5, sm: 1.5 }}
                                sx={{ alignItems: { sm: 'flex-start' } }}
                            >
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <InstallmentStatusSelect
                                        name={`installments.${index}.statuses`}
                                        usedStatuses={values.installments.flatMap(
                                            (other, otherIndex) =>
                                                otherIndex === index ? [] : other.statuses
                                        )}
                                        disabled={disabled}
                                    />
                                </Box>
                                <Box sx={{ width: { xs: '100%', sm: 140 }, flexShrink: 0 }}>
                                    <FormField
                                        type="number"
                                        name={`installments.${index}.percentage`}
                                        label="serviceProviderBilling.schedule.share"
                                        required
                                        disabled={disabled}
                                    />
                                </Box>
                                <Stack
                                    direction="row"
                                    spacing={0.5}
                                    sx={{
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        minHeight: 40,
                                        width: { sm: 150 },
                                        flexShrink: 0,
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
                                    >
                                        {formatCurrency(
                                            getInstallmentAmount(
                                                price,
                                                Number(installment.percentage) || 0
                                            ),
                                            values.currency
                                        )}
                                    </Typography>
                                    <Tooltip
                                        title={translate('serviceProviderBilling.schedule.remove')}
                                    >
                                        <span>
                                            <IconButton
                                                size="small"
                                                aria-label={translate(
                                                    'serviceProviderBilling.schedule.remove'
                                                )}
                                                disabled={
                                                    disabled || values.installments.length === 1
                                                }
                                                onClick={() => remove(index)}
                                            >
                                                <DeleteOutlined fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </Stack>
                            </Stack>
                        ))}
                        <Box>
                            <Button
                                size="small"
                                startIcon={<AddOutlined />}
                                disabled={
                                    disabled ||
                                    values.installments.length >= BILLABLE_CASE_STATUSES.length
                                }
                                onClick={() => {
                                    const next: BillingInstallmentFormValues = {
                                        statuses: [],
                                        percentage: String(
                                            getRemainingPercentage(values.installments)
                                        ),
                                    };
                                    push(next);
                                }}
                                sx={{ textTransform: 'none' }}
                            >
                                {translate('serviceProviderBilling.schedule.add')}
                            </Button>
                        </Box>
                    </Stack>
                )}
            </FieldArray>

            <Paper
                variant="outlined"
                sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(248, 250, 252, 0.55)' }}
            >
                <Stack spacing={1.25}>
                    <Stack
                        direction="row"
                        spacing={1}
                        sx={{ alignItems: 'center', justifyContent: 'space-between' }}
                    >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {translate('serviceProviderBilling.schedule.total')}
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                                {formatCurrency(
                                    getInstallmentAmount(price, total),
                                    values.currency
                                )}
                            </Typography>
                            <Chip
                                size="small"
                                color={total === 100 ? 'success' : 'error'}
                                label={`${total}%`}
                            />
                        </Stack>
                    </Stack>
                    <BillingScheduleBar installments={preview} />
                    {scheduleError ? (
                        <Typography variant="caption" color="error">
                            {scheduleError}
                        </Typography>
                    ) : null}
                </Stack>
            </Paper>
        </Stack>
    );
};

export default InstallmentsEditor;
